import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import { Image3D } from '~/domain/patient/image3d';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { WorkflowTypes, pseudonymizeState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useCompositeCbctBackend, useSessionID } from '../useBackend';

export type CompositeCbctBackendTaskInput = {
    cbctImage: Image3D;
    baseCtImage: Image3D;
};

const COMPOSITE_CBCT_MODEL_LABEL = 'Composite CBCT';

const COMPOSITE_CBCT_GENERATION_MODEL: GenerationModelDetails = {
    // Mirrors the dcmjs provenance we stamp on the generated composite
    // DICOM so the viewport's ModelDetails shows the right label.
    // Kept as `reg_ar_cc_al` / `composite_cbct` to match the legacy
    // v1 model + customization identity — the v2 endpoint itself
    // encodes the task type, but the generation-model metadata is
    // purely a display label on the result.
    name: 'reg_ar_cc_al',
    label: COMPOSITE_CBCT_MODEL_LABEL,
    customizationName: 'composite_cbct',
};

/**
 * Backend-mode submission of a composite CBCT task through the v2
 * API (`POST /v2/composite_cbct/` with pre-uploaded scan datasets).
 *
 * Tags each slice blob with a `cbct_` / `ct_` filename prefix so the
 * shared v2 job orchestrator (`processV2Job`) can split them into
 * two `/v2/images` datasets and submit the task body
 * `{ task_id, cbct_image: <cbct dataset id>, ct_image: <ct dataset id> }`.
 *
 * Mirrors the anonymize / reidentify flow used by the registration
 * path — anonymisation happens on the blobs before they reach the
 * orchestrator, and the downloaded result files are re-identified
 * inside `onLoad` when the job completes.
 */
export function useCompositeCbctBackendTask(onSuccess?: () => void) {
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const patient = useSelectedPatient();
    const compositeCbctClient = useCompositeCbctBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentPrimaryImage } = useViewportEnvironment();

    const buildOnLoad = (patientId: string) => async (job: Job) => {
        if (!job.results || job.results.length === 0) {
            return;
        }
        const finalFiles = job.payload?.pseudonymized
            ? await reidentifyFiles(job.results)
            : job.results;
        if (!finalFiles) {
            handleError('Re-identification failed for one or more files.');
            return;
        }

        const processedItems = await processCreatedFiles(
            finalFiles,
            COMPOSITE_CBCT_GENERATION_MODEL
        );
        if (!processedItems) {
            return;
        }

        updateJob(job.id, { isImported: true });
        // Load the composite as the new primary image. The backend
        // does not emit a registration bridging CBCT ↔ composite, so
        // we cannot meaningfully attach the CBCT input as secondary
        // for blending — the user can pick one manually via
        // ImageHeader's `+ Blend images` if they want to compare.
        const generatedImage = processedItems.images[0];
        if (generatedImage) {
            setEnvironmentPrimaryImage([generatedImage], patientId);
        }
    };

    const startProcessing = async (input: CompositeCbctBackendTaskInput) => {
        if (isSubmitting) {
            return;
        }
        const { cbctImage, baseCtImage } = input;
        if (!cbctImage || !baseCtImage) {
            handleError('Missing required data for Composite CBCT.');
            return;
        }
        if (cbctImage.seriesUid === baseCtImage.seriesUid) {
            handleError('CBCT and base CT must be different image series.');
            return;
        }
        // Resolve the viewport-side patient id up front: the on-load
        // handler flips primary/secondary, so without a patient id
        // `setEnvironmentPrimaryImage` becomes a no-op while the
        // secondary setter still runs — leaving a mismatched
        // viewport. Bail out loudly instead.
        const patientId = patient?.dataSet.PatientID ?? '';
        if (!patientId) {
            handleError('No patient context available for Composite CBCT.');
            return;
        }

        setIsSubmitting(true);
        try {
            // Session id handling matches `useAutoSegmentation`: the
            // current submission uses the session id the
            // `compositeCbctClient` was memoised with at render time,
            // and `resetSessionID()` is called purely to rotate the
            // Recoil atom for the *next* submission (a re-render
            // after this one will build a fresh client). Keeping
            // uploads and polls on the same session id prevents the
            // orphaned-job class of bug seen in
            // `useCompositeCbctBackendTask`'s v1 implementation.
            resetSessionID();
            const sessionId = compositeCbctClient.sessionID;
            const taskUID = `${sessionId}.${v4().split('-')[0]}`;

            const cbctBlobs = Array.from(cbctImage.getSlicesAsDicomBlobs());
            const baseCtBlobs = Array.from(baseCtImage.getSlicesAsDicomBlobs());

            const cbctBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, cbctBlobs)
                : cbctBlobs;
            const baseCtBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, baseCtBlobs)
                : baseCtBlobs;

            if (!cbctBlobsToUpload) {
                handleError('Anonymization failed for CBCT image files.');
                return;
            }
            if (!baseCtBlobsToUpload) {
                handleError('Anonymization failed for base CT image files.');
                return;
            }

            // The v2 orchestrator splits `filesToUpload` into two
            // datasets by the filename prefix each spec registers —
            // `cbct_` for the CBCT dataset, `ct_` for the base CT.
            // Any other prefix is silently ignored, so these two are
            // load-bearing.
            const filesToUpload = [
                ...cbctBlobsToUpload.map((file, idx) => ({
                    file,
                    filename: `cbct_${idx}.dcm`,
                })),
                ...baseCtBlobsToUpload.map((file, idx) => ({
                    file,
                    filename: `ct_${idx}.dcm`,
                })),
            ];

            const backendParameters: RequestParameters = {
                // v2 submits the task body by dataset ids, not by a
                // model action — keep `taskUID` and `sessionId` so
                // the orchestrator can address the datasets and the
                // task endpoint with the same session id.
                action: 'createCompositeCbctTask',
                taskUID,
                sessionId,
            };

            const newJobDetails: NewJobData = {
                id: sessionId,
                name: COMPOSITE_CBCT_MODEL_LABEL,
                totalItems: filesToUpload.length,
                type: WorkflowTypes.COMPOSITE_CBCT,
                client: compositeCbctClient,
                onLoad: buildOnLoad(patientId),
                payload: {
                    filesToUpload,
                    backendParameters,
                    pseudonymized: pseudonymize,
                },
                task: {
                    model: {
                        name: COMPOSITE_CBCT_GENERATION_MODEL.name,
                        bodyPart: '',
                        label: COMPOSITE_CBCT_MODEL_LABEL,
                        type: 'registration',
                        tags: [],
                        inputModalities: ['CT'],
                        isFreeTextEntryModel: false,
                        version: '',
                        isLatest: true,
                    },
                },
            };

            addJob(newJobDetails);
            onSuccess?.();
        } catch (error) {
            handleError(error, 'Failed to start Composite CBCT task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startProcessing, isSubmitting };
}

export default useCompositeCbctBackendTask;
